import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { getPage } from "../../lib/api";
import { kwh, timestamp } from "../../lib/format";
import { displayEnergy, sessionDuration, sessionStateTone } from "../../lib/sessions";
import type { Session } from "../../lib/types";
import { Pill } from "../ui/Status";
import { Cell, TableFrame, TableHead, TableRow } from "../ui/Table";
import type { Column } from "../ui/Table";
import { MicroLabel, SectionTitle } from "../ui/Text";
import { SessionView } from "./SessionView";
import { useViewParam } from "../../lib/useViewParam";

/**
 * One station's recent sessions, on its detail page.
 *
 * Its own query rather than a field on the charge point response: this is a
 * different table with a different retention story, and folding it into
 * `/charge-points/:id` would put a session join on the request an operator refreshes
 * while power-cycling hardware.
 *
 * Deliberately short. The question here is "what has this station been doing
 * lately", not "show me everything" — the full table with filters is one link away.
 * A row opens the session panel over the station page (`?session=`), so looking at
 * one does not lose the station.
 */

const COLUMNS: readonly Column[] = [
  { key: "started", label: "Started", width: "24%" },
  { key: "identity", label: "Identity" },
  { key: "state", label: "State", width: "14%" },
  { key: "duration", label: "Duration", numeric: true, width: "14%" },
  { key: "energy", label: "kWh", numeric: true, width: "14%" },
];

const TAIL_SIZE = 10;
const POLL_MS = 15_000;

export function RecentSessions({
  chargePointId,
  hideWhenEmpty = false,
}: {
  readonly chargePointId: string;
  /**
   * Renders nothing at all rather than an empty-state paragraph. Set when the page
   * has already explained the absence once — a station that has never connected does
   * not need "no sessions yet" spelled out under its own heading as well.
   */
  readonly hideWhenEmpty?: boolean;
}) {
  const [viewing, setViewing] = useViewParam("session");

  const sessions = useQuery({
    queryKey: ["sessions", { chargePointId, limit: TAIL_SIZE }],
    queryFn: () => getPage<Session>("/sessions", { query: { chargePointId, limit: TAIL_SIZE } }),
    refetchInterval: POLL_MS,
    placeholderData: (previous) => previous,
  });

  const rows = sessions.data?.rows ?? [];
  const now = Date.now();

  if (hideWhenEmpty && rows.length === 0 && !sessions.isPending) return null;

  return (
    <section className="flex flex-col gap-4">
      <SectionTitle
        action={
          <Link
            to={`/sessions?chargePointId=${encodeURIComponent(chargePointId)}`}
            className="text-micro font-semibold uppercase tracking-[0.11em] text-signal underline decoration-1 underline-offset-4 hover:no-underline"
          >
            All sessions
          </Link>
        }
      >
        Recent sessions
      </SectionTitle>

      {rows.length === 0 ? (
        <p className="m-0 max-w-prose text-body text-ink-muted">
          {sessions.isPending ? "Loading." : "No sessions on this station yet."}
        </p>
      ) : (
        <>
          <TableFrame>
            <TableHead columns={COLUMNS} />
            <tbody>
              {rows.map((row, index) => {
                const energy = displayEnergy(row);
                return (
                  <TableRow
                    key={row.id}
                    index={index}
                    onActivate={() => setViewing(row.id)}
                    isFaulted={row.anomalyFlags.length > 0}
                  >
                    <Cell>{timestamp(row.startedAtCp, row.depotTimezone)}</Cell>
                    <Cell muted={row.identityLabel === null}>{row.identityLabel ?? row.idTag}</Cell>
                    <Cell>
                      <Pill tone={sessionStateTone(row.state)}>{row.state}</Pill>
                    </Cell>
                    <Cell numeric>{sessionDuration(row, now)}</Cell>
                    <Cell numeric muted={energy.wh === null}>
                      {/* Same tilde as the sessions table: an open session's
                          figure is an estimate wherever it appears. */}
                      {energy.wh === null
                        ? "—"
                        : `${energy.isEstimate ? "~" : ""}${kwh(energy.wh)}`}
                    </Cell>
                  </TableRow>
                );
              })}
            </tbody>
          </TableFrame>
          {rows.some((row) => row.state === "active") ? (
            <MicroLabel>A tilde marks a live estimate, not a committed figure.</MicroLabel>
          ) : null}
        </>
      )}

      <SessionView
        sessionId={viewing}
        open={viewing !== ""}
        onOpenChange={(open) => {
          if (!open) setViewing("");
        }}
      />
    </section>
  );
}
